import { c as _c } from "react/compiler-runtime";
/* eslint-disable react/jsx-props-no-spreading */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type JSX } from 'react';

// eslint-disable-next-line import/no-cycle
import { useAnalytics } from './provider';
import type { Properties } from './types';
interface HeartbeatEnrichment {
  domainObjectType?: string;
  domainObjectUuid?: string;
}
interface HeartbeatEvent extends Properties, HeartbeatEnrichment {
  tabId: string;
  startedAt: Date;
  firstAcitivityTime?: Date;
  lastActivityTime?: Date;
  heartbeatId?: string;
  focused: boolean;
  activity: {
    clicksCount: number;
    focusCount: number;
    scrollCount: number;
    keystrokesCount: number;
    pasteCount: number;
    cutCount: number;
    moused: boolean;
    scrolled: boolean;
    unfocusCount: number;
  };
  flags?: string;
}
function createEmptyHeartbeatEvent(tabId: string, appName: string, enrichment?: HeartbeatEnrichment, heartbeatId?: string, flags?: string): HeartbeatEvent {
  return {
    tabId,
    appName,
    startedAt: new Date(),
    heartbeatId,
    domainObjectType: undefined,
    domainObjectUuid: undefined,
    focused: document.visibilityState === 'visible',
    activity: {
      clicksCount: 0,
      focusCount: 0,
      unfocusCount: 0,
      scrollCount: 0,
      pasteCount: 0,
      cutCount: 0,
      keystrokesCount: 0,
      moused: false,
      scrolled: false
    },
    flags,
    ...enrichment
  };
}
interface HeartbeatContextValue {
  enrichDomainObject: (enrichment?: HeartbeatEnrichment) => void;
  setIdentifier: (enrichment?: string) => void;
}
const HeartbeatContext = createContext<HeartbeatContextValue>({
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  enrichDomainObject: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setIdentifier: () => {}
});
interface HeartbeatProviderProps {
  appName: string;
  enabled?: boolean;
  children: JSX.Element;
  flags?: Record<string, boolean>;
}
export function HeartbeatProvider({
  children,
  appName,
  enabled,
  flags
}: HeartbeatProviderProps): JSX.Element {
  const tabId = useRef(Math.random().toString(36).slice(2));
  const [heartbeatEnrichment, setHeartbeatEnrichment] = useState<(HeartbeatEnrichment & {
    hearbeatId?: string;
  }) | undefined>();
  const [heartbeatId, setHeartbeatId] = useState<string | undefined>();
  const enrichDomainObject = useCallback((enrichment?: HeartbeatEnrichment) => {
    setHeartbeatEnrichment(enrichment);
  }, []);
  const enrichIdentifier = useCallback((identifier?: string) => {
    setHeartbeatId(identifier);
  }, []);
  const heartbeatContextValue: HeartbeatContextValue = useMemo(() => ({
    enrichDomainObject,
    setIdentifier: enrichIdentifier
  }), [enrichDomainObject, enrichIdentifier]);
  if (!enabled) {
    return children;
  }
  return <HeartbeatContext.Provider value={heartbeatContextValue}>
      <HeartbeatCollector tabId={tabId.current} enrichment={heartbeatEnrichment} heartbeatId={heartbeatId} appName={appName} flags={flags} />
      {children}
    </HeartbeatContext.Provider>;
}
export function useHeartbeatEnrichment() {
  return useContext(HeartbeatContext);
}
interface HeartbeatEnrichmentProps {
  heartbeatId?: string;
  enrichment?: HeartbeatEnrichment;
  children: JSX.Element;
}
export function HeartbeatEnrichment(t0) {
  const $ = _c(8);
  const {
    enrichment,
    children
  } = t0;
  const {
    enrichDomainObject
  } = useHeartbeatEnrichment();
  let t1;
  if ($[0] !== enrichDomainObject || $[1] !== enrichment?.domainObjectType || $[2] !== enrichment?.domainObjectUuid) {
    t1 = () => {
      enrichDomainObject({
        domainObjectType: enrichment?.domainObjectType,
        domainObjectUuid: enrichment?.domainObjectUuid
      });
    };
    $[0] = enrichDomainObject;
    $[1] = enrichment?.domainObjectType;
    $[2] = enrichment?.domainObjectUuid;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = enrichment?.domainObjectType;
  const t3 = enrichment?.domainObjectUuid;
  let t4;
  if ($[4] !== enrichDomainObject || $[5] !== t2 || $[6] !== t3) {
    t4 = [t2, t3, enrichDomainObject];
    $[4] = enrichDomainObject;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  useEffect(t1, t4);
  return children;
}
interface HeartbeatIdentifierProps {
  identifier?: string;
  children: JSX.Element;
}
export function HeartbeatIdentifier(t0) {
  const $ = _c(4);
  const {
    identifier,
    children
  } = t0;
  const {
    setIdentifier
  } = useHeartbeatEnrichment();
  let t1;
  let t2;
  if ($[0] !== identifier || $[1] !== setIdentifier) {
    t1 = () => {
      setIdentifier(identifier);
    };
    t2 = [identifier, setIdentifier];
    $[0] = identifier;
    $[1] = setIdentifier;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  return children;
}
interface HeartbeatCollectorProps {
  tabId: string;
  enrichment?: HeartbeatEnrichment;
  heartbeatId?: string;
  appName: string;
  flags?: Record<string, boolean>;
}
export function HeartbeatCollector(t0) {
  const $ = _c(27);
  const {
    tabId,
    enrichment,
    heartbeatId,
    appName,
    flags
  } = t0;
  const analytics = useAnalytics();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const flagsRef = useRef(t1);
  let t2;
  if ($[1] !== appName || $[2] !== enrichment || $[3] !== heartbeatId || $[4] !== tabId) {
    t2 = () => createEmptyHeartbeatEvent(tabId, appName, enrichment, heartbeatId);
    $[1] = appName;
    $[2] = enrichment;
    $[3] = heartbeatId;
    $[4] = tabId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const [, setHeartbeatBatch] = useState(t2);
  const interval = useRef(null);
  let t3;
  if ($[6] !== setHeartbeatBatch) {
    t3 = function trackHearbeatEvent(updater, t4, t5) {
      const moused = t4 === undefined ? false : t4;
      const scrolled = t5 === undefined ? false : t5;
      setHeartbeatBatch(currentHeartbeat => updater({
        ...currentHeartbeat,
        firstAcitivityTime: currentHeartbeat.firstAcitivityTime ?? new Date(),
        lastActivityTime: new Date(),
        moused: currentHeartbeat.moused || moused,
        scrolled: currentHeartbeat.scrolled || scrolled
      }));
    };
    $[6] = setHeartbeatBatch;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const trackHearbeatEvent = t3;
  let t4;
  if ($[8] !== enrichment?.domainObjectType || $[9] !== enrichment?.domainObjectUuid || $[10] !== setHeartbeatBatch) {
    t4 = () => {
      setHeartbeatBatch(currentHeartbeat_0 => ({
        ...currentHeartbeat_0,
        domainObjectType: enrichment?.domainObjectType,
        domainObjectUuid: enrichment?.domainObjectUuid
      }));
    };
    $[8] = enrichment?.domainObjectType;
    $[9] = enrichment?.domainObjectUuid;
    $[10] = setHeartbeatBatch;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== enrichment) {
    t5 = [enrichment];
    $[12] = enrichment;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[14] !== flags) {
    t6 = () => {
      flagsRef.current = flags ?? {};
    };
    t7 = [flags];
    $[14] = flags;
    $[15] = t6;
    $[16] = t7;
  } else {
    t6 = $[15];
    t7 = $[16];
  }
  useEffect(t6, t7);
  let t8;
  if ($[17] !== trackHearbeatEvent) {
    t8 = () => {
      const clickListener = function clickListener() {
        trackHearbeatEvent(_temp, true);
      };
      const keystrokeListener = function keystrokeListener() {
        trackHearbeatEvent(_temp2);
      };
      const mouseMoveListener = function mouseMoveListener() {
        trackHearbeatEvent(_temp3, true);
      };
      const wheelListener = function wheelListener() {
        trackHearbeatEvent(_temp4, undefined, true);
      };
      const pasteListener = function pasteListener() {
        trackHearbeatEvent(_temp5);
      };
      const cutListener = function cutListener() {
        trackHearbeatEvent(_temp6);
      };
      const focusListener = function focusListener() {
        trackHearbeatEvent(_temp7);
      };
      document.addEventListener("click", clickListener);
      document.addEventListener("mousemove", mouseMoveListener);
      document.addEventListener("keypress", keystrokeListener);
      document.addEventListener("wheel", wheelListener);
      document.addEventListener("paste", pasteListener);
      document.addEventListener("cut", cutListener);
      document.addEventListener("visibilitychange", focusListener);
      return () => {
        document.removeEventListener("click", clickListener);
        document.removeEventListener("mousemove", mouseMoveListener);
        document.removeEventListener("keypress", keystrokeListener);
        document.removeEventListener("wheel", wheelListener);
        document.removeEventListener("paste", pasteListener);
        document.removeEventListener("cut", cutListener);
        document.removeEventListener("visibilitychange", focusListener);
      };
    };
    $[17] = trackHearbeatEvent;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = [];
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[20] !== analytics || $[21] !== appName || $[22] !== heartbeatId || $[23] !== setHeartbeatBatch || $[24] !== tabId) {
    t10 = () => {
      if (interval.current) {
        return;
      }
      interval.current = setInterval(() => {
        setHeartbeatBatch(prev_6 => {
          analytics.track({
            event: "heartbeat",
            properties: {
              ...prev_6,
              heartbeatId,
              domainObjectType: prev_6?.domainObjectType,
              domainObjectUuid: prev_6?.domainObjectUuid,
              flags: JSON.stringify(flagsRef.current)
            } as HeartbeatEvent
          });
          return createEmptyHeartbeatEvent(tabId, appName, {
            domainObjectType: prev_6?.domainObjectType,
            domainObjectUuid: prev_6?.domainObjectUuid
          }, heartbeatId);
        });
      }, 5000);
      const flagsCurrent = flagsRef.current;
      return () => {
        if (interval.current) {
          clearInterval(interval.current);
          interval.current = null;
        }
        setHeartbeatBatch(prev_7 => {
          analytics.track({
            event: "heartbeat",
            properties: {
              ...prev_7,
              heartbeatId: prev_7.heartbeatId,
              domainObjectType: prev_7?.domainObjectType,
              domainObjectUuid: prev_7?.domainObjectUuid,
              flags: JSON.stringify(flagsCurrent)
            } as HeartbeatEvent
          });
          return createEmptyHeartbeatEvent(tabId, appName, {
            domainObjectType: prev_7?.domainObjectType,
            domainObjectUuid: prev_7?.domainObjectUuid
          }, heartbeatId);
        });
      };
    };
    t11 = [setHeartbeatBatch, analytics, tabId, heartbeatId, appName];
    $[20] = analytics;
    $[21] = appName;
    $[22] = heartbeatId;
    $[23] = setHeartbeatBatch;
    $[24] = tabId;
    $[25] = t10;
    $[26] = t11;
  } else {
    t10 = $[25];
    t11 = $[26];
  }
  useEffect(t10, t11);
  return null;
}
function _temp7(prev_5) {
  return {
    ...prev_5,
    activity: {
      ...prev_5.activity,
      focusCount: document.visibilityState === "visible" ? prev_5.activity.focusCount + 1 : prev_5.activity.focusCount,
      unfocusCount: document.visibilityState === "hidden" ? prev_5.activity.unfocusCount + 1 : prev_5.activity.unfocusCount
    }
  };
}
function _temp6(prev_4) {
  return {
    ...prev_4,
    activity: {
      ...prev_4.activity,
      cutCount: prev_4.activity.cutCount + 1
    }
  };
}
function _temp5(prev_3) {
  return {
    ...prev_3,
    activity: {
      ...prev_3.activity,
      pasteCount: prev_3.activity.pasteCount + 1
    }
  };
}
function _temp4(prev_2) {
  return {
    ...prev_2,
    activity: {
      ...prev_2.activity,
      scrollCount: prev_2.activity.scrollCount + 1,
      scrolled: true
    }
  };
}
function _temp3(prev_1) {
  return prev_1;
}
function _temp2(prev_0) {
  return {
    ...prev_0,
    activity: {
      ...prev_0.activity,
      keystrokesCount: prev_0.activity.keystrokesCount + 1
    }
  };
}
function _temp(prev) {
  return {
    ...prev,
    activity: {
      ...prev.activity,
      clicksCount: prev.activity.clicksCount + 1
    }
  };
}